import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, ChevronDown, ArrowRight } from 'lucide-react';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [expandedItem, setExpandedItem] = useState(null);
  const headerRef = useRef(null);

  const closeMenu = useCallback(() => {
    setIsMenuOpen(false);
    setExpandedItem(null);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeMenu();
    };
    if (isMenuOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isMenuOpen, closeMenu]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (headerRef.current && !headerRef.current.contains(e.target)) {
        closeMenu();
      }
    };
    if (isMenuOpen || expandedItem) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMenuOpen, closeMenu, expandedItem]);

  const toggleItem = (name) => {
    setExpandedItem(expandedItem === name ? null : name);
  };

  const navItems = [
    {
      label: 'Platform',
      subItems: [
        { label: 'Features', to: '/features' },
        { label: 'Integrations', to: '/integrations' },
        { label: 'Developers', to: '/api-docs' },
        { label: 'Fraud Detection', to: '/anti-fraud' },
        { label: 'Inactivity Mgmt', to: '/inactivity-management' },
      ],
    },
    { label: 'Solutions', to: '/solutions' },
    { label: 'Pricing', to: '/pricing' },
    {
      label: 'Resources',
      subItems: [
        { label: 'Blog', href: 'https://blog.promotly.cloud' },
        { label: 'Help Center', to: '/help' },
        { label: 'Contact Us', to: '/contact' },
      ],
    },
    {
      label: 'Company',
      subItems: [
        { label: 'About Us', to: '/about' },
        { label: 'Careers', to: '/careers' },
        { label: 'Contact', to: '/contact' },
      ],
    },
  ];

  const HEADER_HEIGHT = 80;

  return (
    <header ref={headerRef} className="sticky top-0 z-50 bg-white shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between" style={{ height: `${HEADER_HEIGHT}px` }}>
          <Link to="/" aria-label="Back to homepage" className="no-underline flex items-center shrink-0">
            <img src="/promotly-logo.webp" alt="PROMOTLY" className="h-14 w-auto sm:h-20" />
          </Link>

          {/* Desktop Nav — hidden on <1024px */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navItems.map((item) => (
              <div key={item.label} className="relative group">
                {item.to ? (
                  <Link
                    to={item.to}
                    className="px-3 py-2 text-sm font-medium text-gray-600 hover:text-teal-600 rounded-lg hover:bg-teal-50 transition-colors inline-flex items-center gap-1"
                  >
                    {item.label}
                    {item.subItems && <ChevronDown className="w-3.5 h-3.5" />}
                  </Link>
                ) : (
                  <div>
                    <button
                      onClick={() => toggleItem(item.label)}
                      className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors inline-flex items-center gap-1 ${
                        expandedItem === item.label
                          ? 'text-teal-600 bg-teal-50'
                          : 'text-gray-600 hover:text-teal-600 hover:bg-teal-50'
                      }`}
                    >
                      {item.label}
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${expandedItem === item.label ? 'rotate-180' : ''}`} />
                    </button>
                    <div className={`absolute left-0 top-full pt-1 transition-all duration-150 ${
                      expandedItem === item.label
                        ? 'opacity-100 visible'
                        : 'opacity-0 invisible'
                    }`}>
                      <div className="bg-white rounded-xl shadow-xl border border-gray-100 py-2 min-w-[180px]">
                        {item.subItems.map((sub) =>
                          sub.href ? (
                            <a
                              key={sub.label}
                              href={sub.href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block px-4 py-2.5 text-sm text-gray-600 hover:text-teal-600 hover:bg-teal-50 transition-colors"
                            >
                              {sub.label}
                            </a>
                          ) : (
                            <Link
                              key={sub.label}
                              to={sub.to}
                              className="block px-4 py-2.5 text-sm text-gray-600 hover:text-teal-600 hover:bg-teal-50 transition-colors"
                            >
                              {sub.label}
                            </Link>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Desktop CTA Buttons — hidden on <1024px */}
          <div className="hidden lg:flex items-center gap-3">
            <a
              href="https://cal.com/ahmed-abdulazzim/promotly-demo"
              className="px-4 py-2 text-sm font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded-lg transition-colors no-underline inline-flex items-center gap-1.5"
            >
              Book a Demo
              <ArrowRight className="w-3.5 h-3.5" />
            </a>
            <Link
              to="/new-tenant-signup"
              className="inline-flex items-center justify-center px-5 py-2.5 text-sm font-semibold text-teal-600 bg-transparent border border-teal-600 rounded-md hover:bg-teal-50 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 transition-colors no-underline"
            >
              Start Free Trial
            </Link>
          </div>

          {/* Mobile Hamburger — hidden on >=1024px */}
          <button
            onClick={() => setIsMenuOpen(prev => !prev)}
            className="lg:hidden relative w-10 h-10 flex items-center justify-center rounded-lg text-gray-700 hover:bg-gray-100 transition-all duration-200"
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <div
        className={`fixed left-0 right-0 z-40 bg-white transition-all duration-300 ease-in-out ${
          isMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        style={{ top: `${HEADER_HEIGHT}px`, bottom: 0 }}
      >
        <div className="flex flex-col items-center h-full overflow-y-auto">
          <div className="flex flex-col items-center justify-center flex-1 w-full max-w-xs px-6">
            <nav className="w-full flex flex-col items-center space-y-1">
              {navItems.map((item) => (
                <div key={item.label} className="w-full">
                  {item.to ? (
                    <Link
                      to={item.to}
                      onClick={closeMenu}
                      className="block text-center px-4 py-4 rounded-xl text-gray-700 hover:bg-blue-50 hover:text-blue-600 font-medium text-lg transition-all duration-150"
                    >
                      {item.label}
                    </Link>
                  ) : (
                    <div>
                      <button
                        onClick={() => toggleItem(item.label)}
                        className="flex items-center justify-center gap-2 w-full px-4 py-4 rounded-xl text-gray-700 hover:bg-blue-50 hover:text-blue-600 font-medium text-lg transition-all duration-150"
                      >
                        <span>{item.label}</span>
                        <ChevronDown
                          className={`w-4 h-4 transition-transform duration-200 ${
                            expandedItem === item.label ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                      <div
                        className={`overflow-hidden transition-all duration-200 ${
                          expandedItem === item.label ? 'max-h-96' : 'max-h-0'
                        }`}
                      >
                        <div className="flex flex-col items-center space-y-1 py-2">
                          {item.subItems.map((sub) =>
                            sub.href ? (
                              <a
                                key={sub.label}
                                href={sub.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={closeMenu}
                                className="block text-center px-6 py-3 rounded-lg text-gray-500 hover:bg-blue-50 hover:text-blue-600 text-sm transition-all duration-150"
                              >
                                {sub.label}
                              </a>
                            ) : (
                              <Link
                                key={sub.label}
                                to={sub.to}
                                onClick={closeMenu}
                                className="block text-center px-6 py-3 rounded-lg text-gray-500 hover:bg-blue-50 hover:text-blue-600 text-sm transition-all duration-150"
                              >
                                {sub.label}
                              </Link>
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </nav>

            <div className="mt-10 pt-6 border-t border-gray-100 w-full flex justify-center">
              <a
                href="https://promotly.cloud/"
                className="inline-flex items-center gap-2 px-4 py-2 text-sm text-gray-500 hover:text-blue-600 transition-colors"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                </svg>
                English
              </a>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
